import { apiRequest } from '@/lib/api';

// ============ Types ============

export type CallSource = 'upload' | 'browser' | 'twilio' | 'zoom' | 'meet' | 'teams';
export type CallStatus = 'pending' | 'processing' | 'transcribed' | 'analyzed' | 'failed';
export type Sentiment = 'positive' | 'neutral' | 'negative' | 'mixed';
export type ScorecardFramework = 'meddic' | 'bant' | 'spin' | 'custom';
export type HealthTrend = 'improving' | 'stable' | 'declining';

export interface Call {
  id: string;
  project_id: string;
  build_id: number;
  title: string | null;
  source: CallSource;
  duration_seconds: number | null;
  audio_r2_key: string | null;
  odoo_partner_id: number | null;
  odoo_opportunity_id: number | null;
  status: CallStatus;
  error_message: string | null;
  call_date: string;
  created_at: string;
  updated_at: string;
}

export interface TranscriptSegment {
  id: string;
  call_id: string;
  speaker_id: string | null;
  speaker_name: string | null;
  start_time_ms: number | null;
  end_time_ms: number | null;
  text: string;
  confidence: number | null;
}

export interface ActionItem {
  description: string;
  assignee?: string;
  due_date?: string;
  priority?: 'high' | 'medium' | 'low';
}

export interface KeyQuote {
  text: string;
  speaker?: string;
  timestamp_ms?: number;
  context?: string;
}

export interface CallAnalysis {
  id: string;
  call_id: string;
  summary: string | null;
  action_items: ActionItem[];
  sentiment: Sentiment | null;
  sentiment_score: number | null;
  topics: string[];
  talk_ratio: Record<string, number>;
  objections: string[];
  competitors_mentioned: string[];
  next_steps: string | null;
  key_quotes: KeyQuote[];
  created_at: string;
}

export interface MEDDICScores {
  metrics: number;
  economic_buyer: number;
  decision_criteria: number;
  decision_process: number;
  identify_pain: number;
  champion: number;
}

export interface EvidenceClip {
  segment_id: string;
  text: string;
  start_time_ms: number;
  category: string;
}

export interface CoachingScorecard {
  id: string;
  call_id: string;
  framework: ScorecardFramework;
  scores: MEDDICScores | Record<string, number>;
  overall_score: number | null;
  is_ai_generated: boolean;
  feedback: string | null;
  evidence: EvidenceClip[];
  reviewer_id: number | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface RiskFactor {
  type: string;
  severity: 'high' | 'medium' | 'low';
  description: string;
  evidence?: string;
}

export interface RecommendedAction {
  action: string;
  priority: 'high' | 'medium' | 'low';
  reason: string;
}

export interface DealIntelligence {
  id: string;
  project_id: string;
  odoo_opportunity_id: number;
  health_score: number | null;
  health_trend: HealthTrend | null;
  risk_factors: RiskFactor[];
  total_calls: number;
  total_duration_seconds: number;
  last_call_date: string | null;
  last_call_id: string | null;
  avg_sentiment_score: number | null;
  stakeholders_engaged: number;
  recommended_actions: RecommendedAction[];
  created_at: string;
  updated_at: string;
}

export interface CallWithDetails extends Call {
  transcript: TranscriptSegment[];
  analysis: CallAnalysis | null;
  scorecards: CoachingScorecard[];
}

// ============ API Response Types ============

export interface CallsListResponse {
  calls: Call[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateCallInput {
  title?: string;
  source: CallSource;
  buildId: number;
  callDate?: string;
  odooPartnerId?: number;
  odooOpportunityId?: number;
}

export interface UploadUrlResponse {
  callId: string;
  r2Key: string;
  uploadEndpoint: string;
  contentType: string;
}

export interface UploadResponse {
  success: boolean;
  r2Key: string;
  size: number;
}

// ============ API Functions ============

export async function getCalls(
  projectId: string,
  limit: number = 50,
  offset: number = 0
): Promise<CallsListResponse> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/calls?limit=${limit}&offset=${offset}`);
}

export async function createCall(
  projectId: string,
  input: CreateCallInput
): Promise<Call> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/calls`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getCall(callId: string, includeDetails: boolean = false): Promise<Call | CallWithDetails> {
  const url = includeDetails
    ? `/api/ai/agent/calls/${callId}?details=true`
    : `/api/ai/agent/calls/${callId}`;
  return apiRequest(url);
}

export async function deleteCall(callId: string): Promise<{ success: boolean }> {
  return apiRequest(`/api/ai/agent/calls/${callId}`, {
    method: 'DELETE',
  });
}

export async function getUploadUrl(callId: string, contentType: string = 'audio/webm'): Promise<UploadUrlResponse> {
  return apiRequest(`/api/ai/agent/calls/${callId}/upload-url?contentType=${encodeURIComponent(contentType)}`);
}

export async function uploadAudio(callId: string, audioBlob: Blob): Promise<UploadResponse> {
  const response = await fetch(`/api/ai/agent/calls/${callId}/upload`, {
    method: 'POST',
    headers: {
      'Content-Type': audioBlob.type || 'audio/webm',
    },
    body: audioBlob,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.error || 'Upload failed');
  }

  return response.json();
}

// ============ WebSocket Token ============

export interface WsTokenResponse {
  token: string;
  expiresIn: number;
}

export async function getInsightsWsToken(
  projectId: string,
  buildId: number
): Promise<WsTokenResponse> {
  return apiRequest(`/api/ai/ws-token/${projectId}/${buildId}`);
}

// ============ Processing Progress Types ============

export type ProcessingPhase = 'uploading' | 'transcribing' | 'analyzing' | 'scoring' | 'complete';

export interface ProcessingProgress {
  phase: ProcessingPhase;
  progress: number;
  message: string;
}

export interface ProcessingResult {
  transcript: TranscriptSegment[];
  analysis: CallAnalysis;
  scorecard: CoachingScorecard;
}

// ============ Utility Functions ============

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function getStatusColor(status: CallStatus): string {
  switch (status) {
    case 'pending':
      return 'text-gray-500';
    case 'processing':
      return 'text-blue-500';
    case 'transcribed':
      return 'text-yellow-500';
    case 'analyzed':
      return 'text-green-500';
    case 'failed':
      return 'text-red-500';
    default:
      return 'text-gray-500';
  }
}

export function getSentimentColor(sentiment: Sentiment | null): string {
  switch (sentiment) {
    case 'positive':
      return 'text-green-500';
    case 'neutral':
      return 'text-gray-500';
    case 'negative':
      return 'text-red-500';
    case 'mixed':
      return 'text-yellow-500';
    default:
      return 'text-gray-400';
  }
}

export function getHealthScoreColor(score: number | null): string {
  if (score === null) return 'text-gray-400';
  if (score >= 70) return 'text-green-500';
  if (score >= 40) return 'text-yellow-500';
  return 'text-red-500';
}

// ============ Email & Chatter Types ============

export type InsightStatus = 'pending' | 'processing' | 'analyzed' | 'failed';
export type MessageType = 'email' | 'comment' | 'notification';
export type MessageDirection = 'inbound' | 'outbound' | 'internal';
export type UrgencyLevel = 'low' | 'medium' | 'high' | 'critical';

export interface Recipient {
  id: number;
  name: string;
  email: string;
}

export interface EmailInsight {
  id: string;
  project_id: string;
  build_id: number;
  odoo_message_id: number;
  odoo_model: string;
  odoo_res_id: number;
  subject: string | null;
  body_text: string | null;
  author_id: number | null;
  author_name: string | null;
  recipients: Recipient[] | null;
  message_type: MessageType;
  direction: MessageDirection;
  message_date: string;
  status: InsightStatus;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChatterInsight {
  id: string;
  project_id: string;
  build_id: number;
  odoo_message_id: number;
  odoo_model: string;
  odoo_res_id: number;
  body_text: string | null;
  author_id: number | null;
  author_name: string | null;
  message_date: string;
  status: InsightStatus;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface TextAnalysis {
  id: string;
  insight_type: 'email' | 'chatter';
  insight_id: string;
  summary: string | null;
  sentiment: Sentiment | null;
  sentiment_score: number | null;
  topics: string[];
  action_items: ActionItem[];
  key_points: string[];
  urgency_level: UrgencyLevel | null;
  response_suggested: boolean;
  suggested_response: string | null;
  created_at: string;
}

export interface EmailInsightWithAnalysis extends EmailInsight {
  analysis: TextAnalysis | null;
}

export interface ChatterInsightWithAnalysis extends ChatterInsight {
  analysis: TextAnalysis | null;
}

export interface EmailsListResponse {
  emails: EmailInsight[];
  total: number;
  limit: number;
  offset: number;
}

export interface ChatterListResponse {
  chatter: ChatterInsight[];
  total: number;
  limit: number;
  offset: number;
}

// ============ Email API Functions ============

export async function getEmails(
  projectId: string,
  limit: number = 50,
  offset: number = 0
): Promise<EmailsListResponse> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/emails?limit=${limit}&offset=${offset}`);
}

export async function getEmail(emailId: string, includeAnalysis: boolean = false): Promise<EmailInsight | EmailInsightWithAnalysis> {
  const url = includeAnalysis
    ? `/api/ai/agent/emails/${emailId}?analysis=true`
    : `/api/ai/agent/emails/${emailId}`;
  return apiRequest(url);
}

export interface CreateEmailInput {
  buildId: number;
  odooMessageId: number;
  odooModel: string;
  odooResId: number;
  subject?: string;
  bodyText?: string;
  authorId?: number;
  authorName?: string;
  recipients?: Recipient[];
  messageType: MessageType;
  direction: MessageDirection;
  messageDate: string;
}

export interface BulkCreateEmailsInput {
  buildId: number;
  emails: Array<Omit<CreateEmailInput, 'buildId'>>;
}

export async function createEmail(
  projectId: string,
  input: CreateEmailInput
): Promise<EmailInsight> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/emails`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function bulkCreateEmails(
  projectId: string,
  input: BulkCreateEmailsInput
): Promise<{ success: boolean; count: number }> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/emails`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ============ Chatter API Functions ============

export async function getChatter(
  projectId: string,
  limit: number = 50,
  offset: number = 0
): Promise<ChatterListResponse> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/chatter?limit=${limit}&offset=${offset}`);
}

export async function getChatterInsight(chatterId: string, includeAnalysis: boolean = false): Promise<ChatterInsight | ChatterInsightWithAnalysis> {
  const url = includeAnalysis
    ? `/api/ai/agent/chatter/${chatterId}?analysis=true`
    : `/api/ai/agent/chatter/${chatterId}`;
  return apiRequest(url);
}

export interface CreateChatterInput {
  buildId: number;
  odooMessageId: number;
  odooModel: string;
  odooResId: number;
  bodyText?: string;
  authorId?: number;
  authorName?: string;
  messageDate: string;
}

export interface BulkCreateChatterInput {
  buildId: number;
  notes: Array<Omit<CreateChatterInput, 'buildId'>>;
}

export async function createChatter(
  projectId: string,
  input: CreateChatterInput
): Promise<ChatterInsight> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/chatter`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function bulkCreateChatter(
  projectId: string,
  input: BulkCreateChatterInput
): Promise<{ success: boolean; count: number }> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/chatter`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ============ Email/Chatter Utility Functions ============

export function getInsightStatusColor(status: InsightStatus): string {
  switch (status) {
    case 'pending':
      return 'text-gray-500';
    case 'processing':
      return 'text-blue-500';
    case 'analyzed':
      return 'text-green-500';
    case 'failed':
      return 'text-red-500';
    default:
      return 'text-gray-500';
  }
}

export function getUrgencyColor(urgency: UrgencyLevel | null): string {
  switch (urgency) {
    case 'low':
      return 'text-gray-500';
    case 'medium':
      return 'text-yellow-500';
    case 'high':
      return 'text-orange-500';
    case 'critical':
      return 'text-red-600';
    default:
      return 'text-gray-400';
  }
}

export function getDirectionLabel(direction: MessageDirection): string {
  switch (direction) {
    case 'inbound':
      return 'Received';
    case 'outbound':
      return 'Sent';
    case 'internal':
      return 'Internal';
    default:
      return direction;
  }
}

// ============ Fetch from Odoo API Functions ============

export interface FetchFromOdooInput {
  buildId: number;
  limit?: number;
  model?: string; // Filter by Odoo model (e.g., 'crm.lead')
  resId?: number; // Filter by specific record
}

export interface FetchFromOdooResponse {
  success: boolean;
  count: number;
  message: string;
  error?: string;
}

export async function fetchEmailsFromOdoo(
  projectId: string,
  input: FetchFromOdooInput
): Promise<FetchFromOdooResponse> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/insights/fetch-emails`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function fetchChatterFromOdoo(
  projectId: string,
  input: FetchFromOdooInput
): Promise<FetchFromOdooResponse> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/insights/fetch-chatter`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ============ AI Analysis API Functions ============

export interface AnalyzeEmailResponse {
  success: boolean;
  email: EmailInsightWithAnalysis;
}

export interface AnalyzeAllEmailsResponse {
  success: boolean;
  analyzed: number;
  failed: number;
  remaining: number;
  message: string;
}

export async function analyzeEmail(emailId: string): Promise<AnalyzeEmailResponse> {
  return apiRequest(`/api/ai/agent/emails/${emailId}/analyze`, {
    method: 'POST',
  });
}

export async function analyzeAllEmails(projectId: string): Promise<AnalyzeAllEmailsResponse> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/emails/analyze-all`, {
    method: 'POST',
  });
}

// ============ Chatter AI Analysis API Functions ============

export interface AnalyzeChatterResponse {
  success: boolean;
  chatter: ChatterInsightWithAnalysis;
}

export interface AnalyzeAllChatterResponse {
  success: boolean;
  analyzed: number;
  failed: number;
  remaining: number;
  message: string;
}

export async function analyzeChatter(chatterId: string): Promise<AnalyzeChatterResponse> {
  return apiRequest(`/api/ai/agent/chatter/${chatterId}/analyze`, {
    method: 'POST',
  });
}

export async function analyzeAllChatter(projectId: string): Promise<AnalyzeAllChatterResponse> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/chatter/analyze-all`, {
    method: 'POST',
  });
}

// ============ Customer Intelligence Types ============

export interface CustomerInsightSummary {
  partner_id: number;
  partner_name: string;
  total_emails: number;
  total_chatter: number;
  total_calls: number;
  total_communications: number;
  avg_sentiment_score: number | null;
  sentiment_distribution: {
    positive: number;
    neutral: number;
    negative: number;
    mixed: number;
  };
  overall_sentiment: 'positive' | 'neutral' | 'negative' | 'mixed' | null;
  first_communication: string | null;
  last_communication: string | null;
  top_topics: string[];
  open_action_items: number;
  risk_score: number;
}

export interface CustomerDetailedInsight extends CustomerInsightSummary {
  sentiment_trend: Array<{
    date: string;
    avg_score: number;
    count: number;
  }>;
  recent_communications: Array<{
    id: string;
    type: 'email' | 'chatter' | 'call';
    date: string;
    subject: string | null;
    sentiment: string | null;
    summary: string | null;
  }>;
  all_topics: Array<{
    topic: string;
    count: number;
  }>;
  action_items: Array<{
    description: string;
    priority: string;
    source_type: 'email' | 'chatter';
    source_id: string;
    created_at: string;
  }>;
}

export interface CustomersListResponse {
  customers: CustomerInsightSummary[];
  total: number;
}

// ============ Customer Intelligence API Functions ============

export async function getCustomers(
  projectId: string,
  limit: number = 50,
  offset: number = 0
): Promise<CustomersListResponse> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/customers?limit=${limit}&offset=${offset}`);
}

export async function getCustomerDetail(
  projectId: string,
  partnerId: number
): Promise<CustomerDetailedInsight> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/customers/${partnerId}`);
}

// ============ Customer Utility Functions ============

export function getRiskScoreColor(score: number): string {
  if (score >= 70) return 'text-red-600';
  if (score >= 40) return 'text-yellow-600';
  return 'text-green-600';
}

export function getRiskScoreLabel(score: number): string {
  if (score >= 70) return 'High Risk';
  if (score >= 40) return 'Medium Risk';
  return 'Low Risk';
}

// ============ Retry Failed ============

export interface RetryResponse {
  success: boolean;
  reset?: number;
  message: string;
}

export interface FailedCounts {
  emails: number;
  chatter: number;
}

export async function retryFailedEmails(projectId: string): Promise<RetryResponse> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/emails/retry-failed`, {
    method: 'POST',
  });
}

export async function retryFailedChatter(projectId: string): Promise<RetryResponse> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/chatter/retry-failed`, {
    method: 'POST',
  });
}

export async function retrySingleEmail(emailId: string): Promise<RetryResponse> {
  return apiRequest(`/api/ai/agent/emails/${emailId}/retry`, {
    method: 'POST',
  });
}

export async function retrySingleChatter(chatterId: string): Promise<RetryResponse> {
  return apiRequest(`/api/ai/agent/chatter/${chatterId}/retry`, {
    method: 'POST',
  });
}

export async function getFailedCounts(projectId: string): Promise<FailedCounts> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/failed-counts`);
}
